import { lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import PrivateRoutes from './router/PrivateRoutes'
import PublicRoutes from './router/PublicRoutes'
import AdminRoutes from './router/AdminRoutes'
import AuthLayout from './components/layout/AuthLayout'
import MainLayout from './components/layout/MainLayout'
import NotFound from './pages/NotFound'
import Home from './pages/Home'
import { Spinner } from './components/ui/spinner'
import { Toaster } from 'sonner'

// Lazy-loaded components for better performance

//Auth Pages
const Login = lazy(() => import('./pages/auth/Login'))
const Register = lazy(() => import('./pages/auth/Register'))
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'))

//dashboard Pages
const Dashboard = lazy(() => import('./pages/dashboard/Index'))

//admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const Profile = lazy(() => import('./pages/dashboard/Profile'))
const Settings = lazy(() => import('./pages/dashboard/Settings'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminSettings = lazy(() => import('./pages/admin/Settings'))
const RideManagement = lazy(() => import('./pages/admin/RideManagement'))
const RatingsandFeedback = lazy(
	() => import('./pages/admin/RatingsandFeedback'),
)
const DriverManagement = lazy(() => import('./pages/admin/DriverManagement'))
const PassengerManagement = lazy(
	() => import('./pages/admin/PassengerManagement'),
)
const AdministratorManagement = lazy(
	() => import('./pages/admin/AdministratorManagement'),
)
const InWalletDetails = lazy(() => import('./pages/admin/InwalletDetails'))
const WalletManagement = lazy(() => import('./pages/admin/WalletManagement'))
const WalletDetailsPage = lazy(() => import('./pages/admin/WalletDetailsPage'))
const AdminCommunications = lazy(() => import('./pages/admin/Communications'))
const AdminFinances = lazy(() => import('./pages/admin/Finances'))
const IPRSData = lazy(() => import('./pages/admin/IPRSData')) // Assuming this is the path for IPRSData
const Pricing = lazy(() => import('./pages/admin/Pricing'))

const App = () => {
	const { isAuthenticated, user, isLoading } = useAuth()

	if (isLoading) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<Spinner showSpinner />
			</div>
		)
	}

	return (
		<>
			<Routes>
				{/* Public Routes */}
				<Route element={<PublicRoutes />}>
					<Route element={<AuthLayout />}>
						<Route path="/login" element={<Login />} />
						<Route path="/register" element={<Register />} />
						<Route path="/forgot-password" element={<ForgotPassword />} />
						<Route path="/reset-password" element={<ResetPassword />} />
					</Route>
					<Route
						path="/"
						element={
							isAuthenticated ? (
								//To uncomment once firebase auth is implemented
								// <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />
								<Navigate to="/admin" replace />
							) : (
								<Home />
							)
						}
					/>
				</Route>

				{/* Protected Routes - All authenticated routes under single layout */}
				<Route element={<PrivateRoutes />}>
					<Route element={<MainLayout />}>
						{/* Regular User Routes */}
						<Route path="/dashboard" element={<Dashboard />} />
						<Route path="/profile" element={<Profile />} />
						<Route path="/settings" element={<Settings />} />

						{/* Admin Routes - using AdminRoutes component for authorization */}
						<Route element={<AdminRoutes />}>
							<Route path="/admin" element={<AdminDashboard />} />
							<Route path="/admin/profile" element={<Profile />} />
							<Route path="/admin/rides" element={<RideManagement />} />
							<Route path="/admin/ratings" element={<RatingsandFeedback />} />
							<Route path="/admin/drivers" element={<DriverManagement />} />
							<Route
								path="/admin/passengers"
								element={<PassengerManagement />}
							/>
							<Route
								path="/admin/administrators"
								element={<AdministratorManagement />}
							/>
							{/* <Route path="/admin/wallet" element={<InWalletDetails />} /> */}
							<Route path="/admin/wallets" element={<WalletManagement />} />
							<Route
								path="/admin/wallets/:id"
								element={<WalletDetailsPage />}
							/>
							<Route path="/admin/users" element={<AdminUsers />} />
							<Route path="/admin/settings" element={<AdminSettings />} />
							<Route
								path="/admin/communications"
								element={<AdminCommunications />}
							/>
							<Route path="/admin/finance" element={<AdminFinances />} />
							<Route path="/admin/iprs" element={<IPRSData />} />
							<Route path="/admin/pricing" element={<Pricing />} />
						</Route>
					</Route>
				</Route>

				{/* 404 page */}
				<Route path="*" element={<NotFound />} />
			</Routes>
			<Toaster position="top-center" closeButton />
		</>
	)
}

export default App
